import { Divider } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import Link from '@material-ui/core/Link';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import FaroParametersTable from '../../../faro/parameters/FaroParametersTable';
import ChipContainer from '../../../common/ChipContainer';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 1
    },
    largeGutter: {
      marginBottom: 16,
    },
    section: {
      marginBottom: 24
    },
    paramSetTable: {
      tableLayout: 'fixed'
    },
    topSection: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        '&>:first-child': {
          paddingRight: 8
        },
        '&>:nth-child(2)': {
          paddingLeft: 8
        }
      }
    },
    lambdaSettingsSection: {
      flex: 1,
      marginBottom: 16,
      [theme.breakpoints.up('md')]: {
        marginBottom: 0
      }
    },
    detailsSection: {
      flex: 1
    },
  })
);

interface Props {
  action: any;
  accountId: string;
  region: string;
}

function WorkflowTestActionDetails(props: Props) {
  const { action, accountId, region } = props;
  const classes = useStyles({});

  return (
    <div className={classes.root}>
      <div className={classes.topSection}>
        <div className={classes.detailsSection}>
          <Typography color="textSecondary">Details</Typography>
          <Divider light className={classes.largeGutter} />
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Test Name</Typography>
            <Typography color="textSecondary">{action.TestName}</Typography>
          </div>
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Test Version</Typography>
            <Typography color="textSecondary">{action.Version}</Typography>
          </div>
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Dependencies</Typography>
            {action.Dependencies.length === 0 && <Typography color="textSecondary">None</Typography>}
            <ChipContainer>
              {action.Dependencies.map(dependency => <Chip key={dependency} label={dependency} variant="outlined" size="small" />)}
            </ChipContainer>
          </div>
        </div>
        <div className={classes.lambdaSettingsSection}>
          <Typography color="textSecondary">Lambda Settings</Typography>
          <Divider light className={classes.largeGutter} />
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Security Groups</Typography>
            {(!action.SecurityGroups || action.SecurityGroups.length === 0) && <Typography color="textSecondary">None</Typography>}
            <ChipContainer>
              {action.SecurityGroups && action.SecurityGroups.map(sg => <Chip key={sg} label={sg} variant="outlined" size="small" />)}
            </ChipContainer>
          </div>
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Subnets</Typography>
            {(!action.Subnets || action.Subnets.length === 0) && <Typography color="textSecondary">None</Typography>}
            <ChipContainer>
              {action.Subnets && action.Subnets.map(subnet => <Chip key={subnet} label={subnet} variant="outlined" size="small" />)}
            </ChipContainer>
          </div>
        </div>
      </div>
      <div className={classes.section}>
        <Typography color="textSecondary">IAM Role</Typography>
        <Divider light className={classes.largeGutter} />
        <div className={classes.largeGutter}>
          <Typography>Role to Assume</Typography>
          <Typography color="textSecondary">{action.AssumeRoleARN || 'None - default test role will be used'}</Typography>
        </div>
      </div>
      <div className={classes.section}>
        <Typography color="textSecondary">Parameters</Typography>
        <Divider light className={classes.largeGutter} />
        {(!action.Parameters || Object.keys(action.Parameters).length === 0) && <Typography>None</Typography>}
        {action.Parameters && Object.keys(action.Parameters).length > 0 &&
          <FaroParametersTable hideKmsKeyArn items={Object.keys(action.Parameters).map(key => ({ name: key, value: action.Parameters[key] }))} />}
      </div>
      <div>
        <Typography color="textSecondary">Parameter Sets</Typography>
        <Divider light className={classes.largeGutter} />
        {action.ParameterSets.length === 0 && <Typography>None</Typography>}
        {action.ParameterSets.length > 0 && <Table size="small" className={classes.paramSetTable}>
          <TableHead>
            <TableRow>
              <TableCell>Parameter Set Name</TableCell>
              <TableCell>Parameter Set Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {action.ParameterSets.map(paramSet => <TableRow key={paramSet.Name}>
              <TableCell>
                <Link
                  variant="body1"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/faro/parameters/${accountId}/${region}/${paramSet.Name}?version=${paramSet.Version}`}
                >
                  {paramSet.Name}
                </Link>
              </TableCell>
              <TableCell>{paramSet.Version}</TableCell>
            </TableRow>)}
          </TableBody>
        </Table>}
      </div>
    </div>
  );
};

export default WorkflowTestActionDetails;
